/**
 * Export utilities for batch results
 * Designed to be non-blocking and memory-efficient
 */

/**
 * Export results to JSON format
 */
export const exportToJSON = (data, fileName = 'grading-results') => {
  return new Promise((resolve) => {
    // Use setTimeout to avoid blocking UI
    setTimeout(() => {
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      resolve();
    }, 0);
  });
};

/**
 * Export results to CSV format
 */
export const exportToCSV = (batchData, fileName = 'grading-results') => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      try {
        if (!batchData.results || !Array.isArray(batchData.results)) {
          throw new Error('No results data available');
        }

        // Create CSV headers
        const headers = [
          'Question Number',
          'Student Answer',
          'Correct Answer',
          'Similarity Score',
          'Score',
          'Max Score',
          'Percentage',
          'Status',
          'Feedback'
        ];

        // Create CSV rows
        const rows = batchData.results.map(item => {
          const score = item.value || 0;
          const maxScore = item.maxScore || 1;
          const percentage = maxScore > 0 ? (score / maxScore * 100).toFixed(2) : '0.00';
          const status = score >= maxScore * 0.6 ? 'PASS' : 'FAIL';
          
          // Escape CSV special characters
          const escapeCSV = (str) => {
            if (str === null || str === undefined) return '';
            const string = String(str);
            if (string.includes(',') || string.includes('"') || string.includes('\n')) {
              return `"${string.replace(/"/g, '""')}"`;
            }
            return string;
          };

          return [
            escapeCSV(item.questionNumber),
            escapeCSV(item.studentAnswer),
            escapeCSV(item.correctAnswer),
            escapeCSV(item.similarityScore),
            escapeCSV(score),
            escapeCSV(maxScore),
            escapeCSV(percentage),
            escapeCSV(status),
            escapeCSV(item.feedback)
          ].join(',');
        });

        // Combine headers and rows
        const csvContent = [headers.join(','), ...rows].join('\n');
        
        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        resolve();
      } catch (error) {
        reject(error);
      }
    }, 0);
  });
};

/**
 * Generate PDF report (simplified - in production, use a proper PDF library)
 */
export const exportToPDF = async (batchData, fileName = 'grading-report') => {
  // For now, we'll create a simple HTML report that can be printed
  // In a production environment, you would use a library like jsPDF or pdfmake
  
  return new Promise((resolve) => {
    setTimeout(() => {
      // Create a printable HTML document
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to generate PDF report');
        resolve();
        return;
      }

      const stats = calculateStats(batchData.results || []);
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Grading Report - ${batchData.batchName || 'Batch'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            .header { text-align: center; margin-bottom: 30px; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
            .stat-box { padding: 15px; border: 1px solid #ddd; border-radius: 5px; text-align: center; }
            .stat-value { font-size: 24px; font-weight: bold; }
            .stat-label { font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .pass { color: green; }
            .fail { color: red; }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>AI Grading Report</h1>
            <h2>${batchData.batchName || 'Batch Results'}</h2>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
          
          <div class="stats">
            <div class="stat-box">
              <div class="stat-value">${stats.total}</div>
              <div class="stat-label">Total Answers</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${stats.average}%</div>
              <div class="stat-label">Average Score</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${stats.passed}</div>
              <div class="stat-label">Passed</div>
            </div>
            <div class="stat-box">
              <div class="stat-value">${stats.passRate}%</div>
              <div class="stat-label">Pass Rate</div>
            </div>
          </div>
          
          <h3>Detailed Results</h3>
          <table>
            <thead>
              <tr>
                <th>Question</th>
                <th>Student Answer</th>
                <th>Correct Answer</th>
                <th>Similarity</th>
                <th>Score</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${(batchData.results || []).map(item => {
                const score = item.value || 0;
                const maxScore = item.maxScore || 1;
                const percentage = maxScore > 0 ? (score / maxScore * 100).toFixed(1) : '0';
                const status = score >= maxScore * 0.6 ? 'PASS' : 'FAIL';
                
                return `
                  <tr>
                    <td>${escapeHTML(item.questionNumber || '')}</td>
                    <td>${escapeHTML(item.studentAnswer || '').substring(0, 50)}${(item.studentAnswer || '').length > 50 ? '...' : ''}</td>
                    <td>${escapeHTML(item.correctAnswer || '').substring(0, 50)}${(item.correctAnswer || '').length > 50 ? '...' : ''}</td>
                    <td>${escapeHTML(item.similarityScore || '')}</td>
                    <td>${score}/${maxScore} (${percentage}%)</td>
                    <td class="${status.toLowerCase()}">${status}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="no-print" style="margin-top: 30px;">
            <button onclick="window.print()">Print Report</button>
            <button onclick="window.close()">Close</button>
          </div>
          
          <script>
            function escapeHTML(str) {
              const div = document.createElement('div');
              div.textContent = str;
              return div.innerHTML;
            }
          </script>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      resolve();
    }, 0);
  });
};

/**
 * Calculate statistics for export
 */
const calculateStats = (results) => {
  const scores = results.map(r => {
    const value = r.value || 0;
    const max = r.maxScore || 1;
    return max > 0 ? (value / max) * 100 : 0;
  });
  
  const sum = scores.reduce((a, b) => a + b, 0);
  const avg = scores.length > 0 ? sum / scores.length : 0;
  const passed = scores.filter(s => s >= 60).length;
  
  return {
    total: scores.length,
    average: avg.toFixed(1),
    passed,
    passRate: scores.length > 0 ? ((passed / scores.length) * 100).toFixed(1) : '0.0'
  };
};

/**
 * Main export function that handles all formats
 */
export const exportBatchResults = async (batchData, format = 'json') => {
  const fileName = batchData.batchName || 'grading-results';
  
  switch (format) {
    case 'json':
      await exportToJSON(batchData, fileName);
      break;
    case 'csv':
      await exportToCSV(batchData, fileName);
      break;
    case 'pdf':
      await exportToPDF(batchData, fileName);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
  
  return true;
};
